import { Coins, Rocket, GraduationCap, Clock, BarChart3, Activity, Zap } from 'lucide-react'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { MetricChart } from '@/components/dashboard/MetricChart'
import { usePlatformActivity } from '@/hooks/usePlatformActivity'
import { useDashboardTimeSeries } from '@/hooks/useDashboardTimeSeries'
import { CHART_BLUE, CHART_PURPLE } from '@/lib/chartColors'
import type { TimePeriod } from '@/types/dashboard'

function formatDuration(hours: number): string {
  if (hours === 0) return 'N/A'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${hours.toFixed(1)}h`
  return `${(hours / 24).toFixed(1)}d`
}

interface PlatformActivitySectionProps {
  period: TimePeriod
}

export function PlatformActivitySection({ period }: PlatformActivitySectionProps) {
  const { data, isLoading } = usePlatformActivity(period)
  const { data: timeSeries, isLoading: timeSeriesLoading } = useDashboardTimeSeries(period)

  return (
    <div>
      <DashboardSectionHeader
        icon={Activity}
        title="Platform Activity"
      />
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Tokens Launched"
          value={data?.totalTokens.toLocaleString() || '0'}
          icon={Coins}
          loading={isLoading}
        />
        <StatsCard
          title={period === 'all' ? 'Tokens Launched (all time)' : `Tokens Launched (${period})`}
          value={data?.tokensPeriod.toLocaleString() || '0'}
          change={period !== 'all' && data ? {
            value: Number(data.launchRateTrend.toFixed(1)),
            trend: data.launchRateTrend >= 0 ? 'up' : 'down',
            suffix: '%',
            label: `vs previous ${period}`,
          } : undefined}
          icon={Rocket}
          loading={isLoading}
        />
        <StatsCard
          title="Graduation Rate"
          value={data ? `${data.graduationRate.toFixed(1)}%` : '0.0%'}
          subtitle={data ? `${data.graduatedCount} of ${data.totalTokens} tokens` : undefined}
          icon={GraduationCap}
          loading={isLoading}
        />
        <StatsCard
          title="Avg Time to Graduation"
          value={data ? formatDuration(data.avgTimeToGraduationHours) : 'N/A'}
          icon={Clock}
          loading={isLoading}
        />
        <StatsCard
          title="Total Trading Volume"
          value={data ? `${data.totalTradingVolumeSOL.toFixed(2)} SOL` : '0.00 SOL'}
          icon={BarChart3}
          loading={isLoading}
        />
        <StatsCard
          title={period === 'all' ? 'Trading Volume (all time)' : `Trading Volume (${period})`}
          value={data ? `${data.tradingVolumePeriodSOL.toFixed(2)} SOL` : '0.00 SOL'}
          icon={BarChart3}
          loading={isLoading}
        />
        <StatsCard
          title="Active Bonding Curves"
          value={data?.activeBondingCurves.toLocaleString() || '0'}
          icon={Zap}
          loading={isLoading}
        />
      </div>
      <div className="mt-4 flex flex-col lg:flex-row gap-3 sm:gap-4">
        <div className="flex-1">
          <MetricChart
            type="bar"
            title={period === 'all' ? 'Tokens Launched (all time)' : `Tokens Launched (${period})`}
            data={timeSeries?.launches || []}
            loading={timeSeriesLoading}
          />
        </div>
        <div className="flex-1">
          <MetricChart
            type="line"
            title={period === 'all' ? 'Trading Volume (all time)' : `Trading Volume (${period})`}
            data={timeSeries?.volume || []}
            color={CHART_BLUE}
            valueSuffix=" SOL"
            loading={timeSeriesLoading}
          />
        </div>
      </div>
      <div className="mt-3 sm:mt-4">
        <MetricChart
          type="bar"
          title={period === 'all' ? 'Daily Active Coins (all time)' : `Daily Active Coins (${period})`}
          data={timeSeries?.activeTokens || []}
          color={CHART_PURPLE}
          loading={timeSeriesLoading}
        />
      </div>
    </div>
  )
}
